<template>
  <OptimizationBaseDialog
    v-model="visibility"
    :optimization="props.optimization"
    :person="props.person">
    <template v-if="account">
      <div class="mb-6 w-full rounded border p-3">
        <div
          class="group flex items-center gap-3 rounded p-3 hover:cursor-pointer"
          @click="emit('openAccountDrawer', account)">
          <ApplicationIcon
            :application-name="props.optimization.application_display_name"
            class="shrink-0"
            :size="48"
            :application-type="props.optimization.application_type" />
          <div class="flex flex-col">
            <TextMultiline
              :text="props.optimization.application_display_name"
              class="font-bold" />
            <TextMultiline
              :text="
                i18n.t('licensesCount', {
                  count: Object.keys(account.software_licenses).length,
                })
              " />
            <TextMultiline
              :text="props.person.name ?? props.person.email"
              class="text-gray-400" />
          </div>

          <div
            class="ml-auto mr-4 opacity-0 transition group-hover:opacity-100">
            <SmTooltip :content="i18n.t('showAccount')">
              <ExternalLink :size="22" class="!text-contrast" />
            </SmTooltip>
          </div>
        </div>
      </div>

      <!-- Selection -->
      <div
        v-if="!props.optimization.deprecated"
        class="flex max-h-80 flex-col gap-6 overflow-auto">
        <div
          v-for="parent in (
            data as OptimizationSoftwareAccountRedundantLicenseData
          ).redundant_software_licenses"
          :key="parent.parent_software_license_id"
          class="flex flex-col gap-4">
          <SmTable
            ref="table"
            key-field="_id"
            :data="formatIds(parent.redundant_software_license_ids)"
            :columns="columns">
            <template #header>
              <TextMultiline
                :text="`${i18n.t('xContains', {
                  x:
                    account.software_licenses[parent.parent_software_license_id]
                      ?.name ?? parent.parent_software_license_id,
                })}:`"
                class="px-3 font-bold"
                :class="{ 'text-red': validation == false }" />
            </template>
            <template #license="{ row }">
              <TextMultiline
                :text="account.software_licenses[row._id].name ?? row._id" />
            </template>

            <template #action="{ row }">
              <SmSelect
                v-model:selection="changes[row._id]"
                :options="options"
                :label="i18n.t('chooseOptions')"
                trigger-style="plain"
                dropdown-min-width="150px"
                class="cursor-pointer"
                @change="
                  (option) => {
                    if (!option) return
                    setChange(row._id, option)
                  }
                " />
            </template>
          </SmTable>
        </div>
      </div>
    </template>

    <!-- Loader -->
    <div
      v-else-if="loading"
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <SmSkeleton height="48px" width="48px" />
      <div class="flex w-1/2 flex-col gap-1">
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" />
        <SmSkeleton height="14px" width="50%" />
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="group mb-4 flex w-full items-center gap-3 rounded border p-4 hover:cursor-pointer">
      <EmptyState
        class="min-h-14"
        :text="i18n.t('noAccountFound')"
        orientation="horizontal" />
    </div>

    <!-- Footer -->
    <template #footer>
      <SmButton
        v-if="props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('unhide', props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmButton>
      <SmButton
        v-else-if="!props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('hide', props.optimization._id)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmButton>
      <SmButton
        v-if="!props.optimization.deprecated"
        :disabled="!account"
        @click="executeChanges()">
        {{ i18n.t('execute') }}
      </SmButton>
    </template>
  </OptimizationBaseDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import {
    Account,
    LicenceOut,
    OptimizationSoftwareAccountRedundantLicenseData,
    SoftwareLicenseMini,
  } from '@/client'
  import { Option } from '@/components/sm/SmSelect.vue'
  import { AccountStore } from '@/stores/account/account.store'
  import { sendToast } from '@/components/sm/SmNotification'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { Column } from '@/components/sm/SmTable.types'
  import SmTable from '@/components/sm/SmTable.vue'
  import { ExternalLink } from 'lucide-vue-next'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
  })

  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const emit = defineEmits(['hide', 'unhide', 'openAccountDrawer'])

  const { data } = props.optimization

  // ----------------------------------------------------------------------
  // Load Data
  // ----------------------------------------------------------------------

  const account = ref<LicenceOut>()
  const loading = ref(false)

  onMounted(async () => {
    loadAccount()
  })

  function loadAccount() {
    loading.value = true
    const info = data as OptimizationSoftwareAccountRedundantLicenseData
    AccountStore.getAccountById(info.software_account_id).then((res) => {
      if (res) {
        account.value = res
      }
      loading.value = false
    })
  }

  function getCount() {
    const info = props.optimization
      .data as OptimizationSoftwareAccountRedundantLicenseData

    const totalLength = new Set(
      info.redundant_software_licenses.flatMap(
        (item) => item.redundant_software_license_ids
      )
    ).size

    return totalLength
  }

  // ----------------------------------------------------------------------
  // Selection
  // ----------------------------------------------------------------------

  const options = ref<Option<string>[]>([
    {
      label: i18n.t('general.keep'),
      value: 'keep',
    },
    {
      label: i18n.t('general.remove'),
      value: 'remove',
    },
  ])

  const changes = ref<Record<string, Option<string>[]>>({})

  function setChange(licenseId: string, option: Option<string>[]) {
    changes.value[licenseId] = option
  }

  // ----------------------------------------------------------------------
  //  Table
  // ----------------------------------------------------------------------

  type LicenseIds = {
    _id: string
  }

  function formatIds(licenseIds: string[]): LicenseIds[] {
    return licenseIds.map((id) => ({ _id: id }))
  }

  const table = ref<ComponentExposed<typeof SmTable<LicenseIds[]>>>()

  const columns = computed<Column<LicenseIds>[]>(() => [
    {
      key: 'license',
      label: i18n.t('license'),
      sortable: false,
      width: 1,
      loader: true,
    },
    {
      key: 'action',
      label: '',
      width: 'auto',
      sortable: false,
    },
  ])

  // ----------------------------------------------------------------------
  // Execute changes
  // ----------------------------------------------------------------------

  const validation = ref<boolean>()
  function executeChanges() {
    validation.value = validate()

    if (validation.value) {
      const softwareLicenses: Record<string, SoftwareLicenseMini | null> = {}

      for (const id in changes.value) {
        if (changes.value[id][0].value === 'remove') {
          softwareLicenses[id] = null
        }
      }
      AccountStore.updateAccount(
        (
          props.optimization
            .data as OptimizationSoftwareAccountRedundantLicenseData
        ).software_account_id,
        { software_licenses: softwareLicenses }
      ).then((res) => {
        if (res) {
          sendToast(
            i18n.t(
              'notifications.applicationsDetailView.licences.saveLicencesSuccess.message'
            ),
            undefined,
            'success'
          )

          OptimizationStore.refreshOptimizationCacheFromApi({}, false, false)
          visibility.value = false
        }
      })
    } else {
      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licences.saveLicencesFailed.message'
        ),
        i18n.t('chooseSelectionMessage'),
        'error'
      )
      console.error(`Accounts could not be saved`)
    }
  }

  function validate(): boolean {
    if (account.value === null) return false
    return Object.keys(changes.value).length === getCount()
  }
</script>
